import React from 'react'
import { Dialog, Separator, SizableText, XStack, YStack } from '@mythical/ui'
import { Button } from 'app/components/primitive/Button'
import { IconClose } from 'app/components/primitive/Icon'
import { Avatar } from 'app/components/primitive/Avatar'
import { Tag } from 'app/components/primitive/Tag/Tag'
import { format } from 'date-fns'
import Spinner from 'app/components/primitive/Spinner'

interface ProfileStatsDialogProps {
  userId: string
  open: boolean
  onOpenChange: (value: boolean) => void
}

interface ProfileStatsProps {
  user: {
    fullName: string
    profileImage: string
    tier: string
  }
  topPercentile: number
  rankTopPercentile: number
  streakTopPercentile: number
  videosWatched: number
  videosRated: number
  commentCount: number
  seriesWatched: number
  currentDailyStreak: number
  bestDailyStreak: number
  membershipStartDate: string
}

const mockUser: ProfileStatsProps = {
  user: {
    fullName: 'Jhon Doe with a ver long name',
    profileImage: 'https://img.clock.co.uk/100x100',
    tier: '3rd Degree',
  },
  topPercentile: 25,
  rankTopPercentile: 25,
  streakTopPercentile: 10,
  videosWatched: 100,
  videosRated: 19,
  commentCount: 100,
  seriesWatched: 24,
  currentDailyStreak: 25,
  bestDailyStreak: 100,
  membershipStartDate: new Date().toISOString(),
}

export const ProfileStatsDialog = (props: ProfileStatsDialogProps) => {
  const { userId, open, onOpenChange } = props

  if (!open) return null

  const userStats = mockUser
  const loading = false

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay
          key="overlay"
          animation="normal"
          bg="$backdrop"
          opacity={0.33}
          enterStyle={{ opacity: 0 }}
          exitStyle={{ opacity: 0 }}
          onPress={() => onOpenChange(false)}
        />
        <Dialog.Content
          key="content"
          p="$3.5"
          maxWidth={380}
          w="100%"
          m="$3.5"
          bg="$transparent"
          elevation={0}
          shadowColor="$transparent"
          animateOnly={['transform', 'opacity']}
          animation={[
            'normal',
            {
              opacity: {
                overshootClamping: true,
              },
            },
          ]}
          enterStyle={{ x: 0, y: -100, opacity: 0 }}
          exitStyle={{ x: 0, y: 100, opacity: 0 }}
        >
          <YStack
            jc="center"
            w="100%"
            bg="$secondary9"
            p="$6"
            br="$3"
            elevation="$4"
            gap="$3.5"
          >
            {userStats && (
              <>
                <XStack gap="$3.5" ai="flex-start" jc="space-between">
                  <XStack ai="flex-start" gap="$3" flex={1}>
                    <Avatar
                      tier={userStats.user.tier}
                      src={userStats.user.profileImage}
                      width={70}
                      height={70}
                    />
                    <YStack flex={1} gap="$2">
                      <SizableText
                        ff="$heading"
                        size="$7"
                        fow="500"
                        color="$white"
                        tt="uppercase"
                      >
                        {userStats.user.fullName}
                      </SizableText>
                      <Tag
                        displaySize="xl"
                        variant="green"
                        textProps={{ ff: '$heading' }}
                      >
                        TOP {userStats.topPercentile}%
                      </Tag>
                    </YStack>
                  </XStack>
                  <YStack gap="$2" jc="flex-end">
                    <YStack gap="$1">
                      <SizableText size="$1" color="$secondary7">
                        Society Rank
                      </SizableText>
                      <SizableText
                        ff="$heading"
                        size="$7"
                        fow="700"
                        color="$white"
                      >
                        TOP {userStats.rankTopPercentile}%
                      </SizableText>
                    </YStack>
                    <YStack gap="$1">
                      <SizableText size="$1" color="$secondary7">
                        Daily Streak Rank
                      </SizableText>
                      <SizableText
                        ff="$heading"
                        size="$7"
                        fow="700"
                        color="$white"
                      >
                        TOP {userStats.streakTopPercentile}%
                      </SizableText>
                    </YStack>
                  </YStack>
                </XStack>
                <Separator theme="secondary8" />
                <XStack m="$-2">
                  <YStack w="50%" p="$2" gap="$3">
                    <Stat
                      label="Videos Watched"
                      value={userStats.videosWatched}
                    />
                    <Stat label="Videos Rated" value={userStats.videosRated} />
                    <Stat label="Comments" value={userStats.commentCount} />
                    <Stat
                      label="Series Watched"
                      value={userStats.seriesWatched}
                    />
                  </YStack>
                  <YStack w="50%" p="$2" gap="$3">
                    <Stat
                      label="Current Daily Streak"
                      value={userStats.currentDailyStreak}
                    />
                    <Stat
                      label="Best Daily Streak"
                      value={userStats.bestDailyStreak}
                    />
                    <Stat
                      label="Membership Level"
                      value={userStats.user.tier}
                    />
                    <Stat
                      label="Membership start"
                      value={format(
                        new Date(userStats.membershipStartDate),
                        'MMM d, yyyy'
                      )}
                    />
                  </YStack>
                </XStack>
              </>
            )}
            {!userStats && loading && (
              <YStack ai="center" jc="center" minHeight={200}>
                <Spinner size="large" />
              </YStack>
            )}
          </YStack>

          <Dialog.Close asChild>
            <Button
              aria-label="Close modal"
              opacity={0}
              icon={<IconClose size={24} />}
            />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  )
}

type StatProps = { label: string; value: string | number }

const Stat = ({ label, value }: StatProps) => {
  return (
    <YStack space="$1">
      <SizableText textTransform="uppercase" size="$2" color="$primary8">
        {label}
      </SizableText>
      <SizableText
        fontFamily="$heading"
        size="$6"
        color="$primary3"
        fontWeight="500"
        textTransform="uppercase"
      >
        {`${value}`}
      </SizableText>
    </YStack>
  )
}
